import { useState, useEffect, useCallback } from 'react'
import type { ConversationPlan } from '@knownow/domain'
import { listPlans } from '@/lib/plan-api-client'
import { getOrganizationBySlug } from '@/lib/org-api-client'
import { supabase } from '@/lib/supabase'

export interface UsePlansResult {
  readonly plans: readonly ConversationPlan[]
  readonly loading: boolean
  /** Removes a plan from local state without waiting for a refetch.
   *  Used to keep the UI in sync after a soft-delete: Supabase Realtime does
   *  not deliver UPDATE events for rows that become unreadable to the
   *  subscriber (RLS filters out `deleted_at IS NOT NULL`), so the channel
   *  alone won't notice the change. */
  readonly removePlan: (planId: string) => void
  /** Forces a fresh listPlans() call. */
  readonly refetch: () => void
}

export function usePlans(orgSlug: string | undefined): UsePlansResult {
  const [plans, setPlans] = useState<readonly ConversationPlan[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const fetchPlans = useCallback(async (slug: string): Promise<void> => {
    const result = await listPlans(slug)
    setPlans(result)
    setLoading(false)
  }, [])

  const removePlan = useCallback((planId: string): void => {
    setPlans((prev) => prev.filter((p) => p.id !== planId))
  }, [])

  const refetch = useCallback((): void => {
    if (orgSlug) void fetchPlans(orgSlug)
  }, [orgSlug, fetchPlans])

  useEffect(() => {
    if (!orgSlug) {
      setLoading(false)
      return
    }

    void fetchPlans(orgSlug)

    let channelRef: ReturnType<typeof supabase.channel> | null = null

    void getOrganizationBySlug(orgSlug)
      .then((org) => {
        const onChange = (): void => {
          void fetchPlans(orgSlug)
        }

        channelRef = supabase
          .channel(`plans:${org.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'conversation_plans',
              filter: `organization_id=eq.${org.id}`,
            },
            onChange,
          )
          .subscribe()
      })
      .catch((err: unknown) => {
        console.error('Failed to subscribe to plan changes', err)
      })

    return (): void => {
      if (channelRef) {
        void supabase.removeChannel(channelRef)
      }
    }
  }, [orgSlug, fetchPlans])

  return { plans, loading, removePlan, refetch }
}
